/* eslint-disable react/jsx-no-literals -- pre-i18n migration; replace literals with t() and remove this directive (see docs/i18n-migration-plan.md) */
/**
 * NoWeddingPrompt — Global toast/banner that appears when any API call
 * returns 404 + code:NO_WEDDING (the backend's standardised "you need a
 * wedding first" response). Listens for the `vowsera:no-wedding` custom
 * event that the axios response interceptor dispatches.
 *
 * Auto-dismisses after a few seconds, or when the user clicks "Create
 * Wedding" (which navigates to /dashboard/my-wedding).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Toast, ToastContainer, Button } from 'react-bootstrap';

const NoWeddingPrompt = () => {
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState('');
  // `dismissed` sticks the user's "X" choice for the rest of their stay
  // on the current path. Without this, every failing API call (the page
  // typically fires several — checklist, weddings/mine, etc.) re-runs
  // the event handler and pops the toast right back up, which made the
  // X button look broken even though it actually worked.
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Reset the dismissal when the route changes — the prompt should be
  // willing to show again on the next page the user lands on.
  useEffect(() => { setDismissed(false); }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => {
      // Don't nag on the "My Wedding" page itself — the user is clearly
      // already heading there to fix it.
      if (location.pathname.includes('/dashboard/my-wedding')) return;
      if (dismissed) return; // user already closed it on this page
      setMessage(e.detail?.message || 'You need to create your wedding first.');
      setShow(true);
    };
    window.addEventListener('vowsera:no-wedding', handler);
    return () => window.removeEventListener('vowsera:no-wedding', handler);
  }, [location.pathname, dismissed]);

  const close = () => {
    setShow(false);
    setDismissed(true);
  };

  const goCreate = () => {
    setShow(false);
    setDismissed(true);
    navigate('/dashboard/my-wedding');
  };

  return (
    <ToastContainer position="top-end" style={{ zIndex: 9999, marginTop: 20, marginRight: 20 }}>
      <Toast show={show} onClose={close} delay={8000} autohide bg="warning">
        <Toast.Header>
          <strong className="me-auto">💒 Wedding Setup Needed</strong>
        </Toast.Header>
        <Toast.Body className="d-flex flex-column gap-2">
          <div>{message}</div>
          <Button size="sm" variant="primary" onClick={goCreate} className="align-self-start">
            Go to My Wedding
          </Button>
        </Toast.Body>
      </Toast>
    </ToastContainer>
  );
};

export default NoWeddingPrompt;
